import { Modal } from "antd";
import {
  ListagemAutorizacaoPortariaFiltro,
  filtroDefaultPortaria,
} from "./../../hooks/useFiltro";
import { useState, useEffect } from "react";
import { Flex, Box } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import SelectableButton from "ui/components/Button/SelectableButton";
import { TipoAutorizacaoPortariaEnum } from "core/features/portaria/enums";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import colors from "core/resources/theme/colors";
import DateRangePicker from "components/Input/DateRangePicker";
import SelectUnidade from "components/Input/SelectUnidade";
import SelectPessoas from "components/Input/SelectPessoas";
import { TipoEntidadeEnum } from "core/resources/enums/conta";
import Toolbar from "ui/components/Toolbar";
import { Checkbox } from "@chakra-ui/checkbox";

interface ModalFiltroPortariaProps {
  filtro: ListagemAutorizacaoPortariaFiltro;
  onChangeFiltro: (filtro: Partial<ListagemAutorizacaoPortariaFiltro>) => any;
  visible: boolean;
  onClose: () => any;
  tenantId: any;
}

const TagSelectableButton = ({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => any;
  children: React.ReactNode;
}) => (
  <SelectableButton
    selected={selected}
    onClick={onSelect}
    rounded="full"
    fontSize="14px"
    color={selected ? "white" : "black"}
    borderWidth="0"
    borderColor="white"
    selectedColor={colors.blue500}
    unSelectColor={colors.lightGray}
    px={3}
    py={4}
    _hover={{
      background: `${colors.blue500}`,
      color: `${colors.white}`,
    }}
  >
    {children}
  </SelectableButton>
);

function ModalFiltroPortaria({
  filtro,
  onChangeFiltro,
  visible,
  onClose,
  tenantId,
}: ModalFiltroPortariaProps) {
  const [localFiltro, setLocalFiltro] = useState(filtro);

  const onChangeLocalFiltro = (
    filtro: Partial<ListagemAutorizacaoPortariaFiltro>
  ) => {
    setLocalFiltro((state) => ({ ...state, ...filtro }));
  };

  useEffect(() => {
    setLocalFiltro(filtro);
  }, [filtro]);

  return (
    <Modal
      destroyOnClose
      width={"900px"}
      closable={false}
      className="antd-custom-header"
      title={
        <Toolbar title="Filtros avançados" onClose={onClose} border="0px" />
      }
      centered
      footer={
        <Flex gridGap={4} justifyContent="flex-end">
          <OutlineButton onClick={onClose}>Fechar</OutlineButton>
          <OutlineButton
            onClick={() => {
              onChangeFiltro(filtroDefaultPortaria);
              onClose();
            }}
          >
            Limpar filtros
          </OutlineButton>

          <PrimaryButton
            onClick={() => {
              onChangeFiltro(localFiltro);
              onClose();
            }}
          >
            Filtrar
          </PrimaryButton>
        </Flex>
      }
      visible={visible}
      onCancel={onClose}
    >
      <Flex flexDir="column">
        <Box mb={4}>
          <Text fontWeight="bold" mb="10px">
            Período, unidade e morador
          </Text>
          <Flex gap={2} flexWrap="wrap">
            <Flex flex={0.8}>
              <DateRangePicker
                onChange={(dataInicio, dataFim) => {
                  onChangeLocalFiltro({ dataInicio, dataFim });
                }}
                value={{
                  dataInicio: localFiltro.dataInicio,
                  dataFim: localFiltro.dataFim,
                }}
              />
            </Flex>
            <Flex flex={0.8} minW="200px">
              <SelectUnidade
                tenantId={tenantId}
                onChange={(unidadeId) => {
                  setLocalFiltro((state) => ({
                    ...state,
                    unidadeId,
                    pessoaId: undefined,
                  }));
                }}
                allowClear
                value={localFiltro.unidadeId}
              />
            </Flex>
            <Flex flex={1} minW="200px">
              <SelectPessoas
                placeholder="Selecione o morador"
                tipoEntidades={[
                  TipoEntidadeEnum.Condomino,
                  TipoEntidadeEnum.Funcionario,
                ]}
                unidadeId={localFiltro.unidadeId}
                value={localFiltro.pessoaId}
                allowClear
                onChange={(pessoaId) => {
                  setLocalFiltro((state) => ({ ...state, pessoaId }));
                }}
              />
            </Flex>
          </Flex>
        </Box>

        <Text
          fontSize="sm"
          color={colors.black900}
          mb={2}
          mt={6}
          fontWeight="bold"
        >
          Tipos de autorização
        </Text>

        <Flex gridGap={2} flexWrap="wrap">
          <TagSelectableButton
            selected={localFiltro.tipoAutorizacaoId === null}
            onSelect={() => onChangeLocalFiltro({ tipoAutorizacaoId: null })}
          >
            Todas
          </TagSelectableButton>

          <TagSelectableButton
            selected={
              localFiltro.tipoAutorizacaoId ===
              TipoAutorizacaoPortariaEnum.Delivery
            }
            onSelect={() =>
              onChangeLocalFiltro({
                tipoAutorizacaoId: TipoAutorizacaoPortariaEnum.Delivery,
              })
            }
          >
            Delivery
          </TagSelectableButton>

          <TagSelectableButton
            selected={
              localFiltro.tipoAutorizacaoId ===
              TipoAutorizacaoPortariaEnum.Encomenda
            }
            onSelect={() =>
              onChangeLocalFiltro({
                tipoAutorizacaoId: TipoAutorizacaoPortariaEnum.Encomenda,
              })
            }
          >
            Encomenda
          </TagSelectableButton>

          <TagSelectableButton
            selected={
              localFiltro.tipoAutorizacaoId ===
              TipoAutorizacaoPortariaEnum.PrestadorServico
            }
            onSelect={() =>
              onChangeLocalFiltro({
                tipoAutorizacaoId: TipoAutorizacaoPortariaEnum.PrestadorServico,
              })
            }
          >
            Prestador de serviço
          </TagSelectableButton>

          <TagSelectableButton
            selected={
              localFiltro.tipoAutorizacaoId ===
              TipoAutorizacaoPortariaEnum.Transporte
            }
            onSelect={() =>
              onChangeLocalFiltro({
                tipoAutorizacaoId: TipoAutorizacaoPortariaEnum.Transporte,
              })
            }
          >
            Transporte
          </TagSelectableButton>

          <TagSelectableButton
            selected={
              localFiltro.tipoAutorizacaoId ===
              TipoAutorizacaoPortariaEnum.Visitante
            }
            onSelect={() =>
              onChangeLocalFiltro({
                tipoAutorizacaoId: TipoAutorizacaoPortariaEnum.Visitante,
              })
            }
          >
            Visitante
          </TagSelectableButton>

          <TagSelectableButton
            selected={
              localFiltro.tipoAutorizacaoId ===
              TipoAutorizacaoPortariaEnum.FuncionarioDomestico
            }
            onSelect={() =>
              onChangeLocalFiltro({
                tipoAutorizacaoId:
                  TipoAutorizacaoPortariaEnum.FuncionarioDomestico,
              })
            }
          >
            Funcionário Doméstico
          </TagSelectableButton>
        </Flex>

        <Flex mt={6}>
          <Checkbox
            mr="auto"
            isChecked={localFiltro.verConfirmadas === "1"}
            onChange={() =>
              onChangeLocalFiltro({
                verConfirmadas: localFiltro.verConfirmadas === "1" ? "0" : "1",
              })
            }
          >
            Ver autorizações confirmadas
          </Checkbox>
        </Flex>
      </Flex>
    </Modal>
  );
}

export default ModalFiltroPortaria;
